import React, { useState, useCallback, useEffect, useRef } from 'react'

import { BrowserRouter as Router, Route } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { HubConnectionState } from '@microsoft/signalr'

import { simMod, moduleContent } from './Module.config'
import { getUser } from './User'
import { loadScripts, isDebug } from './App.config'
import * as Solver from './Solver'
import * as LRS from './api/api'
import * as LMS from './LMS'
import { ModuleContextProvider, initialSession, SessionType } from './contexts/ModuleContext'
import { LLTCC_Profile, LLTF1_Profile, LLTMC_Profile, LLTS1_Profile, ModuleClass, ModuleProfile, SimutechModule, TS_Profile } from './ModuleTypes'
import { FaultMode } from './Types'
import { storeInSession, sapphireConsoleCSS as logCSS } from './Utils'

import { TroubleshootProvider } from './contexts/TroubleshootContext'
import { LearningLabProvider } from './contexts/LearningLabContext'

import 'bootstrap/dist/css/bootstrap.min.css'
import './styles/index.scss'

import Home from './components/Home'
import Simulation from './components/Simulation'
import Scoreboard from './components/troubleshoot/Scoreboard'
import WorkOrderPage from './components/troubleshoot/WorkOrder'
import PostAssessment from './components/troubleshoot/PostAssessment'
import FaultHistory from './components/troubleshoot/FaultHistory'
import FaultAttempt from './components/troubleshoot/FaultAttempt'
import Dialog from './components/Dialog'
import { calculateScore } from './components/learninglab/LearningLab'

type DialogContent = {
    title: string
    body: string
    closeText: string
    onHide: () => void
}

const App: React.FC = () => {
    const [scriptsLoaded, setScriptsLoaded] = useState(false)
    const [session, setSession] = useState<SessionType>(initialSession)

    const [showDialog, setShowDialog] = useState(false)
    const errorDialog = useRef({} as DialogContent)

    const [moduleScore, setModuleScore] = useState(-1)
    const [updateLRSScore, setUpdateLRSScore] = useState(true)

    const handleUnload = useCallback(() => {
        try {
            const { connection, user, userProfile, sessionGUID, groupName } = session
            console.warn('App unload triggered. Exiting app...')
            LRS.sendTerminateBeacon(user, userProfile['module-registration-id'], sessionGUID)
            Solver.stopConnection(connection, user.userName, groupName)
            if (session.LMSAvailable) {
                LMS.commitData()
                LMS.concedeControl()
            }
        }
        catch (error) {
            console.error(error)
        }
    }, [session])

    useEffect(() => {
        window.addEventListener('unload', handleUnload)
        return () => window.removeEventListener('unload', handleUnload)
    }, [handleUnload])

    const handleCriticalError = useCallback((error: any) => {
        const errorMsg = `An error has occurred.  Please contact your Learning Management System administrator. ${isDebug ? error : ''}`
        console.error(`${errorMsg} ${error}`)
        setSession(prevState => {
            return { ...prevState, errorMessage: errorMsg }
        })
        handleUnload()
    }, [handleUnload])

    const simulationStarted = useCallback((connectionId: string, clientConnectionId: string, groupName: string) => {
        console.log(`%cSimulation ${connectionId} has started and joined group ${groupName}`, Solver.solverCSS)
        storeInSession('groupName', groupName)

        const initUserProfile = async () => {
            const rawProfile = JSON.parse(await LRS.getUserProfile(session.user)).profile
            const userProfile = CastProfile(rawProfile)
            isDebug && console.log('LRS User Profile:')
            isDebug && console.log(userProfile)
            await LRS.sendInitialize(session.user, userProfile['module-registration-id'], session.sessionGUID)

            setSession(prevState => {
                return {
                    ...prevState,
                    solverAvailable: true,
                    userProfile,
                    groupName
                }
            })
            const sessionStart = new Date().getTime()
            storeInSession('sessionStart', JSON.stringify(sessionStart))
        }
        initUserProfile()
            .catch((error) => handleCriticalError(error))

    }, [handleCriticalError, session.sessionGUID, session.user])

    const handleSolverDisconnect = useCallback(() => {
        setShowDialog(false)
        session.LMSAvailable && LMS.concedeControl()
        !session.LMSAvailable && handleCriticalError(`${errorDialog.current.title}: ${errorDialog.current.body}`)
    }, [handleCriticalError, session.LMSAvailable])

    const RegisterSolverCallbacks = useCallback((connection: signalR.HubConnection) => {
        console.log('%cRegistering General Solver Callbacks...', logCSS)
        const solverError = (data: any) => {
            console.error('Client Solver Error: ' + data)
        }
        connection.on('clientSolverError', solverError)
        connection.on('clientSimulationStarted', simulationStarted)
        connection.on('clientConnectionUpdate', (msg: string) => {
            isDebug && console.log('%cClient Connection Update received from Solver: %s', Solver.solverCSS, msg)
        })
        connection.on('clientMessageReceived', (data: any) => {
            isDebug && console.log('%cReceived Ack. %s', Solver.solverCSS, data)
        })
        connection.on('clientMessageUpdate', (data: any) => {
            isDebug && console.log('%cMessage Update received from Solver: %s', Solver.solverCSS, data)
        })
        connection.on('processSolverRequest', (data: any) => {
            isDebug && console.log(`%cProcess Solver Request received:`, Solver.solverCSS)
            isDebug && console.log(data)
        })
        connection.onclose(() => {
            console.warn('SignalR connection closed.', connection)
            errorDialog.current = {
                title: 'Lost connection',
                body: 'Oops... it seems we lost the connection to the server. Please try loading the module again.',
                onHide: handleSolverDisconnect,
                closeText: 'Exit Module'
            }
            setShowDialog(true)
        })
    }, [handleSolverDisconnect, simulationStarted])

    useEffect(() => {
        if (!scriptsLoaded && !session.errorMessage) {
            isDebug && console.log('Load scripts effect.')
            loadScripts()
                .then(() => {
                    const available = LMS.init()
                    if (available) {
                        setSession(prevState => {
                            return {
                                ...prevState,
                                LMSAvailable: true
                            }
                        })
                    }
                    setScriptsLoaded(true)
                })
                .catch((error) => handleCriticalError(error))
        }
    }, [handleCriticalError, scriptsLoaded, session.errorMessage])

    useEffect(() => {
        if (!scriptsLoaded || session.errorMessage || session.user !== initialSession.user) return
        isDebug && console.log('Init effect.')

        const init = async () => {
            LMS.init()
            const user = await getUser()

            setSession(prevState => {
                return {
                    ...prevState,
                    user
                }
            })
        }
        init()
            .catch((error) => handleCriticalError(error))

    }, [handleCriticalError, scriptsLoaded, session.errorMessage, session.user])

    useEffect(() => {
        const { connection, errorMessage, user, sessionGUID } = session
        if ((connection.state === HubConnectionState.Connected) || (errorMessage) || (user === initialSession.user)) return
        isDebug && console.log('Solver init effect.')

        const solverInit = async () => {
            RegisterSolverCallbacks(connection)
            await Solver.startConnection(connection, user, sessionGUID, simMod.solverModule)
        }
        solverInit()
            .catch((error) => {
                console.error(error)
                errorDialog.current = {
                    title: 'Connection failed',
                    body: 'Oops... it seems the connection to the server has failed. Please try loading the module again. If it persists, contact your administrator.',
                    onHide: handleSolverDisconnect,
                    closeText: 'Exit Module'
                }
                setShowDialog(true)
            })

    }, [RegisterSolverCallbacks, handleCriticalError, handleSolverDisconnect, session])

    useEffect(() => {
        const { user, errorMessage, userProfile } = session
        if (simMod.isTS || errorMessage || moduleScore !== -1 || user === initialSession.user || userProfile === initialSession.userProfile) return
        const recalculatedScore = calculateScore(userProfile)
        setModuleScore(recalculatedScore)
    }, [moduleScore, session])

    useEffect(() => {
        if (moduleScore === -1 || !scriptsLoaded || !session.LMSAvailable) return
        isDebug && console.log('Saving score to LMS...')
        if (moduleScore === 100) LMS.setModulePassed() // This also sets score to 100.
        else LMS.setModuleScore(moduleScore)

    }, [moduleScore, scriptsLoaded, session.LMSAvailable])


    useEffect(() => {
        const { errorMessage, user, userProfile } = session
        if (errorMessage || moduleScore === -1 || !updateLRSScore || userProfile['module-score'] === moduleScore) return
        isDebug && console.log('App is updating LRS module score...')

        session.userProfile['module-score'] = moduleScore

        LRS.updateLRSModuleScore(user, moduleScore)
            .then(() => setUpdateLRSScore(false))
            .catch(e => console.error(e))

    }, [moduleScore, session, updateLRSScore])

    if (session.errorMessage) return (
        <div style={{ padding: '2em' }}>
            <div className="alert alert-danger text-center">
                <FontAwesomeIcon icon={faExclamationTriangle} />&nbsp;&nbsp;
            {session.errorMessage}
            </div>
        </div>
    )

    return (
        <>
            <Helmet>
                <title>{moduleContent.title}</title>
                <base href="/" />
            </Helmet>
            <ModuleContextProvider value={session}>
                <TroubleshootProvider>
                    <LearningLabProvider>
                        <Router>
                            <Route path='/' exact={true}> <Home /> </Route>
                            <Route path='/explore'>
                                <Simulation faultMode={FaultMode.Explore} />
                            </Route>
                            <Route path='/scoreboard'> <Scoreboard /> </Route>
                            <Route path='/troubleshoot'> <Scoreboard /> </Route>
                            <Route path='/workorder'> <WorkOrderPage /> </Route>
                            <Route path='/postassessment'> <PostAssessment /> </Route>
                            <Route path='/faulthistory'><FaultHistory /></Route>
                            <Route path='/faultattempt/:id' exact={true}><FaultAttempt /></Route>
                            <Route path='/simulation'>
                                <Simulation faultMode={FaultMode.Troubleshoot} />
                            </Route>
                            <Route path='/learn/:sectionId'><Simulation /></Route>
                        </Router>
                    </LearningLabProvider>
                </TroubleshootProvider>
                <Dialog
                    show={showDialog}
                    title={errorDialog.current.title}
                    body={errorDialog.current.body}
                    onHide={errorDialog.current.onHide}
                    closeText={errorDialog.current.closeText}
                />
            </ModuleContextProvider>
        </>
    )
}

export default App

const CastProfile = (profile: ModuleProfile): ModuleProfile => {
    switch (simMod.solverModule) {
        case SimutechModule.LLTS_1:
            return profile as LLTS1_Profile
        case SimutechModule.LLTMC_1:
            return profile as LLTMC_Profile
        case SimutechModule.LLTCC_5:
            return profile as LLTCC_Profile
        case SimutechModule.LLTF1_4:
            return profile as LLTF1_Profile
        default:
            if (simMod.type !== ModuleClass.TS) throw new Error('Unexpected Module Profile type.')
            return profile as TS_Profile
    }
}
